/** @format */
import React, { useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';

import { useMedia } from 'react-use';

import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress } from '@mui/material';

import { VerifyEmailAddress } from './verify-email-address';
import { WaitForEmailVerification } from './wait-for-email-verification';
import { WhenSignedIn, WhenSignedOut } from './sign_in_out_util';
import { getAccessToken } from 'mydnv/utils/access-token';
import * as signInService from 'mydnv/services/sign-in-service';

import dayjs from 'dayjs';
import { MyDNVContext } from 'mydnv/context/mydnv-context';
import Tooltip from '@mui/material/Tooltip';
import Backdrop from '@mui/material/Backdrop';
import { WrappedNavLink } from './wrapped-navlink';
import { useInterval } from 'react-use';

const MyDnv = () => {
    const {
        InteractionStatusNone,
        displayName,
        emailVerified,
        setEmailVerified,
        email,
        displayNameLoaded,
        showVerifiedTip,
        setShowVerifiedTip,
        setShowVerifySpinner,
        setShowVerifySent,
        account,
        isAuthenticated,

        storeKeyVerifyingEmailAddress,
        storeKeySignOutSignUpTimestamp,

        instance,
        inProgress,
        userAccount,
        isCheckYourEmailModalOpen,
        requestVerificationEmailActionType,
        setRequestVerificationEmailActionType,
        emailVerificationApiCalled,
        setEmailVerificationApiCalled,
        isEmailVerificationModalOpen,
        setIsEmailVerificationModalOpen,
        emailVerificationModalMessage,
        setEmailVerificationModalMessage,
        verifyEmailRequestBody,
        setVerifyEmailRequestBody,
        SignUpRequest,
        LoginRequest,
    } = useContext(MyDNVContext);

    const [dropdownToggleState, setDropdownToggleState] = useState(false);
    const dropdownBtnRef = useRef(null);
    // const [isEmailVerificationModalOpen, setIsEmailVerificationModalOpen] = useState(false);
    // const [emailVerificationModalMessage, setEmailVerificationModalMessage] = useState('');
    // const [verifyEmailRequestBody, setVerifyEmailRequestBody] = useState(null);
    const [getEmailVerifiedToken, setGetEmailVerifiedToken] = useState('');

    // const { token } = useAuth();
    // const loginRequest = Config.MyDNV.loginRequest;
    // const signUpRequest = Config.MyDNV.signUpRequest;
    // const storeKeyVerifyingEmailAddress = 'verifying-email-address';
    // const storeKeySignOutSignUpTimestamp = 'sign-out-sign-up';
    const navigate = useNavigate();

    const isMobileOrTablet = useMedia('screen and (max-width: 1124px)');

    const verifiedRef = useRef(null);

    const request = useMemo(() => {
        return {
            ...LoginRequest,
            account: account,
        };
    }, [account, LoginRequest]);

    const unverifiedBookmark = localStorage.getItem('not-signedin-bookmark');

    const checkBookmark = async () => {
        const bookmarkPage = localStorage.getItem('not-signedin-bookmark');
        if (bookmarkPage !== null) {
            const path = JSON.parse(bookmarkPage).path || '/';
            navigate(path);
        }
    };

    const handleModal = (action) => {
        switch (action) {
            case 'sign-out':
                handleSignOut();
                break;
            case 'sign-out-sign-up':
                localStorage.setItem(storeKeySignOutSignUpTimestamp, JSON.stringify({ timestamp: dayjs().valueOf() }));
                // Need to sign-out first... then go back to sign-up
                handleSignOut();
                break;
            case 'resend-verification-email':
                setRequestVerificationEmailActionType('user-request');
                break;
            case 'close-wait-for-email-verification-modal':
                setIsEmailVerificationModalOpen(false);
                setEmailVerificationModalMessage('');
                setVerifyEmailRequestBody(null);
                setShowVerifiedTip(true);
                // check if user tried to bookmark a page
                checkBookmark();

                // TODO first post email confirmation dialog
                break;
            default:
                break;
        }
    };

    const handleSignIn = () => {
        localStorage.removeItem('not-signedin-bookmark');
        localStorage.removeItem('signin-redirect-action');
        instance.loginRedirect(LoginRequest).catch((e) => {
            console.error(e);
        });
    };

    const handleSignUp = () => {
        localStorage.removeItem('not-signedin-bookmark');
        localStorage.removeItem('signin-redirect-action');
        localStorage.removeItem(storeKeySignOutSignUpTimestamp);
        instance.loginRedirect(SignUpRequest).catch((e) => {
            console.error(e);
        });
    };
    const handleSignOut = () => {
        localStorage.removeItem(storeKeyVerifyingEmailAddress);
        instance
            .logoutRedirect({
                postLogoutRedirectUri: '/',
            })
            .catch((e) => {
                console.error(e);
            });

        setDropdownToggleState(false);
    };

    const goToAboutUs = () => {
        navigate('/what-is-mydnv');
    };

    const handleRequestVerificationEmail = useCallback((args) => {
        signInService
            .postRequestVerificationEmail(args)
            .then((response) => {
                setShowVerifySpinner(false);
                setShowVerifySent(true);
            })
            .then(() => {
                //start polling
                setGetEmailVerifiedToken(args);
            })
            .catch((e) => console.error(e));
    }, []);

    const handleVerifyEmailAddress = useCallback((args) => {
        signInService
            .postVerifyEmailAddress(args)
            .then((response) => {
                localStorage.removeItem(storeKeyVerifyingEmailAddress);
                if (408 === response.status) {
                    setEmailVerificationModalMessage('timeout');
                } else if (200 === response.status) {
                    setEmailVerified(true);
                    setEmailVerificationApiCalled(true);
                    setEmailVerificationModalMessage('verified');
                }
            })
            .catch((e) => console.error(e));
    }, []);

    const handleButtonClickListener = (event) => {
        if (event.type === 'blur' && event.relatedTarget) {
            event.preventDefault();
        } else {
            setDropdownToggleState(!dropdownToggleState);
        }
    };

    useInterval(
        () => {
            signInService.getEmailVerified(getEmailVerifiedToken).then((res) => {
                if (res.data === true) {
                    setGetEmailVerifiedToken(''); // to stop polling
                    setEmailVerificationModalMessage('verified');
                }
            });
        },
        getEmailVerifiedToken !== '' ? 10000 : null
    );

    useEffect(() => {
        if (!!verifyEmailRequestBody) {
            getAccessToken(request, instance, handleVerifyEmailAddress, { body: verifyEmailRequestBody }, null);
        }
    }, [verifyEmailRequestBody, handleVerifyEmailAddress, instance, request]);

    useEffect(() => {
        if (
            isAuthenticated &&
            inProgress === InteractionStatusNone &&
            !!userAccount &&
            !!requestVerificationEmailActionType
        ) {
            getAccessToken(
                request,
                instance,
                handleRequestVerificationEmail,
                { body: { actionType: requestVerificationEmailActionType } },
                null
            );
        }
    }, [
        isAuthenticated,
        inProgress,
        userAccount,
        requestVerificationEmailActionType,
        handleRequestVerificationEmail,
        instance,
        request,
    ]);

    useEffect(() => {
        if (verifiedRef.current && displayNameLoaded && emailVerificationApiCalled && emailVerified) {
            if (showVerifiedTip) {
                verifiedRef.current.focus();
            }
        }
    }, [verifiedRef, showVerifiedTip, displayNameLoaded, emailVerificationApiCalled, emailVerified]);

    const MyDNVButtonLabel = () => {
        return <>MyDNV</>;
    };

    const DisplayNameLoaded = () => {
        return (
            <>
                {emailVerified ? (
                    <>
                        {!isMobileOrTablet && <span className='display-name'>{displayName}</span>}
                        <FontAwesomeIcon icon={`fa-solid fa-user`} size={isMobileOrTablet ? 'lg' : '1x'} />
                    </>
                ) : (
                    <MyDNVButtonLabel />
                )}
            </>
        );
    };

    const handleTooltipClose = () => {
        setShowVerifiedTip(false);
    };

    return (
        <>
            <div className={`myDnvLoginButtonDropdown ${isAuthenticated ? 'loggedIn' : ''}`}>
                <WhenSignedIn>
                    {emailVerificationApiCalled && emailVerified ? (
                        <>
                            <Tooltip
                                ref={verifiedRef}
                                classes={{ popper: 'bookmarks-tooltip' }}
                                open={showVerifiedTip && unverifiedBookmark === null}
                                disableFocusListener
                                disableHoverListener
                                disableTouchListener
                                arrow
                                slotProps={{
                                    popper: {
                                        disablePortal: true,
                                        role: 'dialog',
                                    },
                                }}
                                title={
                                    <React.Fragment>
                                        <h4>Stay informed your way!</h4>
                                        <p>
                                            Customize your watch list and subscriptions to receive updates that matter
                                            to you.
                                        </p>
                                        <button title='Close tooltip' onClick={handleTooltipClose}>
                                            Ok, got it
                                        </button>
                                    </React.Fragment>
                                }
                                placement='bottom-start'
                            >
                                <WrappedNavLink to='/myDNV' className='signedIn'>
                                    {displayNameLoaded ? (
                                        <DisplayNameLoaded />
                                    ) : (
                                        <CircularProgress size='20px' color='primary' />
                                    )}
                                </WrappedNavLink>
                            </Tooltip>

                            <Backdrop
                                sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                                open={showVerifiedTip}
                            ></Backdrop>
                        </>
                    ) : (
                        <div className='signedIn unverified'>
                            {displayNameLoaded ? (
                                <DisplayNameLoaded />
                            ) : (
                                <CircularProgress size='20px' color='primary' />
                            )}
                        </div>
                    )}
                </WhenSignedIn>
                <WhenSignedOut>
                    <button
                        className={`dropBtn ${dropdownToggleState ? 'dropdownActive' : ''}`}
                        id='dropBtn'
                        ref={dropdownBtnRef}
                        onClick={handleButtonClickListener}
                        onBlur={handleButtonClickListener}
                    >
                        <MyDNVButtonLabel />
                    </button>
                </WhenSignedOut>

                <div className={`dropdownBtnContent ${dropdownToggleState ? 'show' : ''}`}>
                    <WhenSignedOut>
                        <button onClick={() => handleSignIn()}>Log in</button>
                        <button onClick={() => handleSignUp()}>Sign up</button>
                        <button onClick={() => goToAboutUs()}>About MyDNV</button>
                    </WhenSignedOut>
                </div>
            </div>

            <VerifyEmailAddress
                isOpen={isCheckYourEmailModalOpen}
                setModalOpen={handleModal}
                emailAddress={email}
                showVerifiedBanner={emailVerificationModalMessage === 'verified'}
            />

            <WaitForEmailVerification
                isOpen={isEmailVerificationModalOpen}
                setModalOpen={handleModal}
                message={emailVerificationModalMessage}
            />
        </>
    );
};

export { MyDnv };
